/* =============================================================
   AboutSection — United Dream Mongolia
   Design: Dark violet theme + anime team illustration above
   ============================================================= */
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Award, Clock, Shield, Star, Users, Globe } from "lucide-react";

const TEAM_IMAGE = "https://d2xsxph8kpxj0f.cloudfront.net/310519663375899735/N7ycErHKT5joBFzKWLPbGM/anime-team-jwnpbMQHStTfRLj9CzAQun.webp";

const features = [
  {
    icon: Award,
    title: "10+ жилийн туршлага",
    desc: "Визийн зөвлөгөө чиглэлд арвин туршлагатай",
  },
  {
    icon: Shield,
    title: "Иж бүрэн дэмжлэг",
    desc: "Баримт бичгээс эхлэн виз гарах хүртэл бүх алхамд дэмжлэнэ",
  },
  {
    icon: Clock,
    title: "Хурдан шуурхай",
    desc: "Процессыг хамгийн богино хугацаанд дуусгана",
  },
  {
    icon: Star,
    title: "Мэргэжлийн баг",
    desc: "Тухайн улс орондоо ажиллаж, аялаж байсан туршлагатай зөвлөхүүд",
  },
];

const stats = [
  { icon: Users, value: "2,000+", label: "Амжилттай үйлчлүүлэгч" },
  { icon: Globe, value: "10+", label: "Улс орон" },
  { icon: Award, value: "10+", label: "Жилийн туршлага" },
];

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="about"
      className="relative overflow-hidden"
      style={{ background: "oklch(0.10 0.06 285)" }}
    >
      {/* ── Anime team illustration ─────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative w-full overflow-hidden"
        style={{ maxHeight: "480px" }}
      >
        {/* Top fade into section bg */}
        <div
          className="absolute top-0 left-0 right-0 h-16 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, oklch(0.10 0.06 285), transparent)",
          }}
        />
        <img
          src={TEAM_IMAGE}
          alt="United Dream Mongolia баг"
          className="w-full object-cover object-top"
          style={{ maxHeight: "480px" }}
        />
        {/* Bottom fade into section */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to top, oklch(0.10 0.06 285) 30%, transparent)",
          }}
        />
      </motion.div>

      {/* Background gradient blobs */}
      <div
        className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "oklch(0.55 0.28 295)" }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl pointer-events-none"
        style={{ background: "oklch(0.52 0.26 285)" }}
      />

      <div className="container relative z-10 pb-20 md:pb-28">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-4"
            style={{
              borderColor: "oklch(0.55 0.28 295 / 0.4)",
              background: "oklch(0.55 0.28 295 / 0.1)",
            }}
          >
            <span className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: "oklch(0.75 0.18 295)" }}
            >
              Бидний тухай
            </span>
          </div>
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: "oklch(0.97 0.01 285)" }}
          >
            Таны Итгэлт{" "}
            <span
              style={{
                background: "linear-gradient(135deg, oklch(0.75 0.22 295), oklch(0.65 0.26 285))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Визийн Зөвлөх
            </span>
          </h2>
          <p
            className="text-base max-w-2xl mx-auto leading-relaxed"
            style={{ color: "oklch(0.70 0.05 285)" }}
          >
            United Dream Mongolia нь 2016 оноос хойш Монголын иргэдэд дэлхийн тэргүүлэх улс
            орнуудад виз авахад туслаж ирсэн. Бид зөвхөн виз авахаас гадна таны амьдралын
            шинэ хуудсыг нээхэд хамтрагч нь болдог.
          </p>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-14 max-w-2xl mx-auto"
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-5 rounded-2xl border"
              style={{
                background: "oklch(0.55 0.28 295 / 0.08)",
                borderColor: "oklch(0.55 0.28 295 / 0.25)",
              }}
            >
              <stat.icon
                className="w-6 h-6 mx-auto mb-2"
                style={{ color: "oklch(0.72 0.20 295)" }}
              />
              <div
                className="text-2xl font-bold mb-0.5"
                style={{ color: "oklch(0.97 0.01 285)" }}
              >
                {stat.value}
              </div>
              <div
                className="text-xs"
                style={{ color: "oklch(0.65 0.06 285)" }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Features grid */}
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}
              className="p-5 rounded-2xl border group transition-all duration-300 hover:-translate-y-1"
              style={{
                background: "oklch(0.55 0.28 295 / 0.07)",
                borderColor: "oklch(0.55 0.28 295 / 0.20)",
              }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ background: "oklch(0.55 0.28 295 / 0.20)" }}
              >
                <feature.icon
                  className="w-5 h-5"
                  style={{ color: "oklch(0.72 0.20 295)" }}
                />
              </div>
              <div
                className="font-semibold text-sm mb-1.5"
                style={{ color: "oklch(0.95 0.02 285)" }}
              >
                {feature.title}
              </div>
              <div
                className="text-xs leading-relaxed"
                style={{ color: "oklch(0.65 0.06 285)" }}
              >
                {feature.desc}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
